import { captureMessage } from '@sentry/react';
import { LOCATION_CHANGED, push } from '@takeshape/redux-little-router';
import { getUserId } from 'app/auth/selectors';
import { getSelected } from 'app/multi-select/selectors';
import type { SagaIterator } from 'redux-saga';
import { all, call, delay, fork, put, race, select, take, takeLatest } from 'redux-saga/effects';
import type { ActionType } from 'typesafe-actions';
import { getUser } from '../auth/actions.js';
import { DEFAULT_BRANCH } from '../branches/constants.js';
import { createBranchPath } from '../branches/utils.js';
import { reportImportStatus } from '../common/saga.js';
import { IMPORT_STATUS } from '../common/types.js';
import handleError from '../common/utils/handle-error.js';
import { getRoutineSaga as getNewRoutineSaga } from '../common/utils/new-sagas.js';
import { getRoutineSaga, handleRoutine } from '../common/utils/saga.js';
import { takeLatestRoutine } from '../common/utils/take-latest-routine.js';
import { mutateAdmin as mutate, setProjectEndpoint } from '../graphql-client.js';
import { showMessage } from '../messages/actions.js';
import { resetForm } from '../schema-form/actions.js';
import {
  DELETE_PROJECT,
  GET_PROJECT_LIST,
  GET_PROJECT_MEMBERS,
  INVITE_USER,
  REMOVE_SELECTED_PROJECT_MEMBERS,
  UPDATE_INVITE_STATUS,
  UPDATE_PROJECT_PERMISSIONS,
  UPLOAD_IMPORT_COMPLETE,
  URI_IMPORT_COMPLETE
} from './action-types';
import {
  createProject,
  getProject,
  getProjectAdmins,
  getProjectList,
  getProjectMembers,
  importProjectFiles,
  importProjectURI,
  type RemoveProjectMemberParams,
  removeProjectMember,
  setCurrentProject,
  setImportStatus,
  updateProjectSettings,
  uploadImportComplete,
  uriImportComplete
} from './actions';
import {
  CREATE_PROJECT_MUTATION,
  cancelProjectInviteMutation,
  deleteProjectMutation,
  getProjectAdminsQuery,
  getProjectListQuery,
  getProjectMembersQuery,
  getProjectQuery,
  IMPORT_PROJECT_URI_MUTATION,
  inviteUser,
  putS3,
  removeProjectMemberMutation,
  UPDATE_PROJECT_SETTINGS_MUTATION,
  UPLOAD_PROJECT_MUTATION,
  updateInviteRoleMutation,
  updateInviteStatus,
  updateProjectPermissionsMutation
} from './api';
import { NAME as MEMBERS_NAME } from './components/members/constants.js';
import { getCurrentProjectId, selectProjectMembers } from './selectors.js';

type Member = {
  role: string;
  email: string;
  id: string;
};

function* _getProjectList(): SagaIterator {
  yield put(getProjectList());
}

export function* watchGetProjectMembers(): SagaIterator {
  yield takeLatestRoutine(GET_PROJECT_MEMBERS, getProjectMembersQuery);
}

/**
 * Wait for user permissions to be updated or timeout
 * @return true when there is a timeout
 */
export const waitForPermissionsUpdateEffect = race([delay(30 * 1000, true), take(getUser.FULFILLED)]);
export function* waitForPermissionsUpdate(): SagaIterator<boolean> {
  const [timeout] = yield waitForPermissionsUpdateEffect;
  return Boolean(timeout);
}

export function* handlePermissionsTimeout(projectId: string): SagaIterator {
  const userId = yield select(getUserId);
  yield call(captureMessage, 'Loading user permissions timed out after project creation', {
    level: 'warning',
    tags: { userId, projectId }
  });
  yield put(getUser.requested());
  yield take([getUser.FULFILLED, getUser.REJECTED]);
}

export const createProjectRequestedEffect = take([createProject.REQUESTED, importProjectURI.REQUESTED]);
export const createProjectFulfilledEffect = take([createProject.FULFILLED, URI_IMPORT_COMPLETE]);
export const createProjectRejectedEffect = take([createProject.REJECTED, importProjectURI.REJECTED]);

export function* watchCreateProjectSaga(): SagaIterator {
  // Since we created the project we need to call getUser to refresh our cookie and access policy
  while (true) {
    yield createProjectRequestedEffect;
    const readyToRedirect = all([createProjectFulfilledEffect, call(waitForPermissionsUpdate)]);
    const [ready] = yield race([readyToRedirect, createProjectRejectedEffect]);
    if (ready) {
      const [createFulfilled, timeout] = ready;
      const projectId: string = createFulfilled.payload.project?.id || createFulfilled.payload.projectId;
      if (timeout) {
        yield call(handlePermissionsTimeout, projectId);
      }

      yield put(push(createBranchPath(projectId)));
    }
  }
}

export function* watchDeleteProject(): SagaIterator {
  yield takeLatestRoutine(DELETE_PROJECT, deleteProjectMutation);
}

export function* watchUpdateProjectPermissions(action: ActionType<any>): SagaIterator {
  try {
    if (action.payload.status === 'Member') {
      yield call(updateProjectPermissionsMutation, action);
    } else {
      yield call(updateInviteRoleMutation, action);
    }

    yield put(getProjectMembers());
    yield put(showMessage('info', `Successfully updated role of ${action.payload.email}.`, 3000));
  } catch (e: any) {
    const errorAction = handleError(e);
    if (errorAction) {
      yield put(errorAction);
    } else {
      yield put(showMessage('error', `Failed to update role of ${action.payload.email}.`));
    }
  }
}

export function* handleRemoveProjectMember(payload: RemoveProjectMemberParams): SagaIterator {
  if (payload.userId === 'invited') {
    yield call(cancelProjectInviteMutation, { payload });
  } else {
    yield call(removeProjectMemberMutation, { payload });
  }

  const userId = yield select(getUserId);
  // If you are removing yourself redirect to project list
  if (payload.userId === userId) {
    yield put(getProjectList());
    yield put(push('/projects'));
  } else {
    yield put(getProjectMembers());
  }
}

export function* watchRemoveSelectedProjectMembers(): SagaIterator {
  const selected = yield select(getSelected, { multiSelectName: MEMBERS_NAME });
  const members = (yield select(selectProjectMembers)).items;
  const owner = members.find((member: Member) => member.role === 'owner');
  for (const member of members) {
    if (selected.has(member.email) && member.id !== owner.id) {
      yield put(
        removeProjectMember.requested({
          userId: member.id,
          email: member.email
        })
      );
    }
  }

  if (selected.has(owner.email)) {
    yield put(showMessage('error', 'Cannot remove project owner.', 3000));
  }
}

function* watchInviteUser(action: ActionType<any>): SagaIterator {
  try {
    yield call(inviteUser as any, action);
    yield put(getProjectMembers());
    yield put(showMessage('info', 'Invite Sent', 3000));
  } catch (e: any) {
    if (e.name === 'Conflict') {
      yield put(
        showMessage('warning', `Invite failed: ${action.payload.email} is already of a member of this project.`)
      );
      return;
    }

    yield put(showMessage('error', 'Invite failed'));
  }
}

export function* watchUpdateInviteStatus(action: ActionType<any>): SagaIterator {
  try {
    yield call(updateInviteStatus as any, action);
    yield put(getProjectList());
  } catch {
    yield put(showMessage('error', `Invite ${action.payload.status} failed`));
  }
}

export function* locationChanged({ payload }: ActionType<any>): SagaIterator {
  const { params } = payload;
  if (params) {
    let { projectId, branch: branchEscaped } = params;
    const branch = branchEscaped ? decodeURIComponent(branchEscaped) : DEFAULT_BRANCH;
    if (params.projectId) {
      const currentProjectId = yield select(getCurrentProjectId);
      if (currentProjectId !== params.projectId) {
        ({ projectId } = params);
        yield put(setCurrentProject({ projectId, branch }));
        yield put(getProject.requested({ projectId }));
      }
    } else {
      yield put(setCurrentProject({ projectId: '', branch: '' }));
    }

    yield call(setProjectEndpoint, projectId, branch);
  }
}

function* showSettingsUpdateFulfilledSaga(): SagaIterator {
  yield put(showMessage('info', 'Project settings updated.', 1500));
}

function* showSettingsUpdateRejectedSaga(): SagaIterator {
  yield put(showMessage('error', 'Failed to update project settings. Please try again.', 3000));
}

const updateProjectSettingsSaga = getRoutineSaga(
  updateProjectSettings,
  mutate(UPDATE_PROJECT_SETTINGS_MUTATION, (arg: any) => {
    const { projectName, avatar, ...args } = arg;
    return {
      ...args,
      name: projectName,
      avatarId: avatar === null ? null : avatar?.id
    };
  })
);

export const importProjectUriMutation = mutate(IMPORT_PROJECT_URI_MUTATION);

export function* importProjectUriSaga(arg: ActionType<any>): SagaIterator {
  yield put(setImportStatus(IMPORT_STATUS.PENDING));
  const { importId } = yield call(importProjectUriMutation, arg.values);
  yield call(reportImportStatus, importId, setImportStatus, uriImportComplete, false);
}

export function* importProjectUriRejected(arg: ActionType<any>): SagaIterator {
  yield put(showMessage('error', arg.payload.message));
  yield put(setImportStatus(IMPORT_STATUS.NOT_STARTED));
}

export const uploadProjectMutation = mutate(UPLOAD_PROJECT_MUTATION);

export function* importProjectFilesSaga(arg: ActionType<any>): SagaIterator {
  const { files } = arg;
  yield put(setImportStatus(IMPORT_STATUS.PENDING));

  const file = files[0];

  let signedUrl;
  let importId;
  try {
    const response = yield call(uploadProjectMutation, {
      name: file.name.replace(/ /g, '_'),
      type: 'application/zip'
    });
    signedUrl = response.upload.uri;
    importId = response.upload.importId;
  } catch (e: any) {
    yield put(showMessage('error', e.message));
    yield put(setImportStatus(IMPORT_STATUS.NOT_STARTED));
    return;
  }

  const response = yield call(putS3 as any, { url: signedUrl, file });

  if (response.status === 200) {
    yield call(reportImportStatus, importId, setImportStatus, uploadImportComplete, false);
  } else {
    yield put(showMessage('error', `Error uploading project. HTTP status: ${response.status}`));
    yield put(setImportStatus(IMPORT_STATUS.NOT_STARTED));
  }
}

export function* handleUploadImportComplete(): SagaIterator {
  yield put(resetForm('projectUploadForm'));
  yield put(getProjectList());
}

export default function* root(): SagaIterator {
  yield all([
    fork(watchGetProjectMembers),
    fork(watchDeleteProject),
    takeLatestRoutine(GET_PROJECT_LIST, getProjectListQuery),
    takeLatest(INVITE_USER, watchInviteUser),
    takeLatest(UPDATE_INVITE_STATUS, watchUpdateInviteStatus),
    takeLatest(UPDATE_PROJECT_PERMISSIONS, watchUpdateProjectPermissions),
    takeLatest(removeProjectMember.REQUESTED, getNewRoutineSaga(removeProjectMember, handleRemoveProjectMember)),
    takeLatest(REMOVE_SELECTED_PROJECT_MEMBERS, watchRemoveSelectedProjectMembers),
    takeLatest(`${DELETE_PROJECT}_FULFILLED`, _getProjectList),
    takeLatest(LOCATION_CHANGED, locationChanged),
    takeLatest(updateProjectSettings.REQUESTED, updateProjectSettingsSaga),
    takeLatest(updateProjectSettings.FULFILLED, showSettingsUpdateFulfilledSaga),
    takeLatest(updateProjectSettings.REJECTED, showSettingsUpdateRejectedSaga),
    takeLatest(getProject.REQUESTED, getRoutineSaga(getProject, getProjectQuery)),
    handleRoutine(getProjectAdmins, getProjectAdminsQuery),
    handleRoutine(
      createProject,
      mutate(CREATE_PROJECT_MUTATION, (payload) => payload?.values)
    ),
    fork(watchCreateProjectSaga),
    handleRoutine(importProjectURI, importProjectUriSaga),
    takeLatest(importProjectURI.REJECTED, importProjectUriRejected),
    handleRoutine(importProjectFiles, importProjectFilesSaga),
    takeLatest(UPLOAD_IMPORT_COMPLETE, handleUploadImportComplete)
  ]);
}
